import { useEffect, useCallback, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../reduxService/hooks';
import { unit as unitEnum, EProjectType } from '../../enum/project';
import { setProject } from '../store/project/projectslice';

// Import modular hooks
import { useProjectData } from './core/useProjectData';
import { useProjectLoader } from './core/useProjectLoader';
import { useProjectTransform } from './core/useProjectTransform';
import { useFormConfiguration } from './form/useFormConfiguration';
import { useFormValidation } from './form/useFormValidation';
import { useFormDialog } from './form/useFormDialog';
import { useUnitConversion } from './conversion/useUnitConversion';
import { useMethodSync } from './effects/useMethodSync';

/**
 * Modularized version of useProjectFormSetup
 * Uses composition and facade pattern to combine smaller, focused hooks
 */
export function useProjectFormSetupModular() {
    const { t } = useTranslation();
    const { id: idFromUrl } = useParams<{ id?: string }>();
    const dispatch = useAppDispatch();

    // Core hooks
    const projectData = useProjectData();
    const projectLoader = useProjectLoader();
    const { transformProjectData } = useProjectTransform();

    // Form hooks
    const formConfig = useFormConfiguration();
    const formValidation = useFormValidation();
    const formDialog = useFormDialog();

    // Conversion hooks
    const unitConversion = useUnitConversion();

    // Effects hooks
    const methodSync = useMethodSync(projectData.projectMethod);

    // Local state for method changing
    const [isMethodChanging, setIsMethodChanging] = useState<boolean>(false);

    // Combined loading state
    const loading = projectLoader.loading || isMethodChanging;

    // Setup form for a specific method
    // Returns the final validated data so callers can use it directly (without waiting for React state)
    const setupFormForMethod = useCallback(
        async (method: string, type: string, targetUnit: unitEnum = unitEnum.SI, projectInfoData?: any): Promise<Record<string, any>> => {
            console.log(`[MODULAR_HOOK] Setting up form for method: ${method} (type: ${type}) for unit: ${targetUnit}`);

            try {
                // Load form configuration
                const configResult = await formConfig.loadFormConfiguration(type, method, projectData.hasBeenRan, projectInfoData);

                // Create validation schema
                formValidation.createValidationSchema(configResult.projectTypeJson, targetUnit, projectData.projectData?.conversionApplied);

                // Validate and structure data
                const structuredData = formValidation.validateAndStructureData(projectInfoData, method, configResult.projectTypeJson);

                // 🔍 DEBUG: Trace surface.minThickness during form setup
                console.log('🔍 DEBUG: setupFormForMethod - after validateAndStructureData', {
                    surfaceMinThicknessInProjectInfoData: (projectInfoData as any)?.surface?.minThickness,
                    surfaceMinThicknessInStructuredData: (structuredData as any)?.surface?.minThickness,
                    targetUnit,
                    conversionApplied: projectData.projectData?.conversionApplied,
                });

                // Handle unit conversion if needed
                let finalData = structuredData;
                if (targetUnit === unitEnum.Imperial) {
                    // 🔧 FIX: For DISPLAY purposes, ALWAYS convert Metric→Imperial when in Imperial mode
                    // The `conversionApplied` flag tracks whether data was converted for STORAGE (Imperial→Metric on submit),
                    // NOT whether we should convert for display. Data in Redux is ALWAYS in Metric after submission,
                    // so we must always convert for display in Imperial mode.
                    //
                    // Flow:
                    // 1. User enters 0.1 ft (Imperial)
                    // 2. Form submit: 0.1 ft → 0.0305 m (Metric), conversionApplied=true
                    // 3. After RUN, form re-setup needs to convert 0.0305 m → 0.1 ft for display
                    // 4. Previously: conversionApplied=true caused shouldConvert() to return false (BUG!)
                    // 5. Now: Always convert for display, only skip Redux tracking update

                    // 🔍 DEBUG: Trace conversion for display
                    console.log('🔍 DEBUG: setupFormForMethod - converting for display in Imperial mode', {
                        surfaceMinThicknessBefore: (structuredData as any)?.surface?.minThickness,
                        conversionApplied: projectData.projectData?.conversionApplied,
                    });

                    // ALWAYS convert Metric→Imperial for display
                    finalData = unitConversion.performConversion(structuredData, method, configResult.flatFields, unitEnum.SI, targetUnit);

                    console.log('🔍 DEBUG: setupFormForMethod - after conversion', {
                        surfaceMinThicknessAfter: (finalData as any)?.surface?.minThickness,
                    });

                    // Only update conversion tracking if not already applied
                    // (This prevents marking as "converted" when loading existing converted data)
                    if (projectData.projectData && !projectData.projectData.conversionApplied) {
                        unitConversion.updateConversionTracking(projectData.projectData, method, finalData);
                    }
                }

                // 🔍 DEBUG: Final data that will be set to form
                console.log('🔍 DEBUG: setupFormForMethod - finalData', {
                    surfaceMinThicknessInFinalData: (finalData as any)?.surface?.minThickness,
                });

                formValidation.setValidatedData(finalData);

                // Return finalData so callers can use it directly without waiting for React state update
                return finalData;
            } catch (error) {
                console.error('[MODULAR_HOOK] Error setting up form:', error);
                formDialog.setErrorDialog('error_setting_up_form');
                formValidation.clearValidation();
                formConfig.setGroupedFields({});
                formConfig.setAccordionTitles({});
                throw error;
            }
        },
        [formConfig, formValidation, unitConversion, projectData.hasBeenRan, projectData.projectData, formDialog]
    );

    // Main effect for loading and setting up the form
    useEffect(() => {
        let isCancelled = false;

        const loadDataAndSetupForm = async () => {
            if (!idFromUrl) {
                console.error('[MODULAR_HOOK] No project ID found in URL');
                if (!isCancelled) {
                    formDialog.setErrorDialog('error_missing_project_id');
                    projectLoader.setLoading(false);
                }
                return;
            }

            if (!isCancelled) {
                projectLoader.setLoading(true);
            }

            // Handle new projects
            if (idFromUrl === 'new') {
                if (projectData.hasProject && projectData.projectType && projectData.projectMethod) {
                    try {
                        await setupFormForMethod(projectData.projectMethod, projectData.projectType, projectData.projectUnit);
                        if (!isCancelled) {
                            formDialog.clearError();
                        }
                    } catch (error) {
                        if (!isCancelled) {
                            console.error('[MODULAR_HOOK] Error setting up new project form:', error);
                        }
                    } finally {
                        if (!isCancelled) {
                            projectLoader.setLoading(false);
                        }
                    }
                } else {
                    if (!isCancelled) {
                        formDialog.setErrorDialog('Project data is incomplete for setup.');
                        projectLoader.setLoading(false);
                    }
                }
                return;
            }

            // Handle existing projects
            if (projectData.hasProject && projectData.projectGuid === idFromUrl) {
                try {
                    if (projectData.projectMethod && projectData.projectType) {
                        await setupFormForMethod(projectData.projectMethod, projectData.projectType, projectData.projectUnit, projectData.projectData?.projectInfo?.data);
                        if (!isCancelled) {
                            formDialog.clearError();
                        }
                    }
                } catch (error) {
                    if (!isCancelled) {
                        console.error('[MODULAR_HOOK] Error setting up form from Redux data:', error);
                    }
                } finally {
                    if (!isCancelled) {
                        projectLoader.setLoading(false);
                    }
                }
            } else {
                // Fetch from API with onSuccess callback for immediate form setup
                // This avoids the race condition of waiting for React re-render
                const success = await projectLoader.loadProject(idFromUrl, transformProjectData, async loadedProject => {
                    if (isCancelled) return;

                    // Setup form immediately with loaded data
                    if (loadedProject.method && loadedProject.type) {
                        try {
                            await setupFormForMethod(loadedProject.method, loadedProject.type, loadedProject.unit || unitEnum.SI, loadedProject.projectInfo?.data);
                            formDialog.clearError();
                        } catch (error) {
                            console.error('[MODULAR_HOOK] Error setting up form after API load:', error);
                        }
                    }
                });
                if (!success && !isCancelled) {
                    projectLoader.setLoading(false);
                }
            }
        };

        loadDataAndSetupForm();

        return () => {
            isCancelled = true;
        };
        // 🔧 FIX: Simplified dependencies to prevent unnecessary re-triggers
        // ONLY idFromUrl triggers re-load. When idFromUrl changes, we need to load new project.
        // For new projects (idFromUrl === 'new'), projectType/projectMethod are checked INSIDE the effect
        // For existing projects, we load by GUID and projectType/projectMethod come from API response
        //
        // WHY NOT projectType/projectMethod in deps?
        // - When setProject() updates Redux, projectType changes → would RE-TRIGGER useEffect
        // - This causes duplicate API call (observed: 2x GET /api/Project/projects/{guid})
        // - projectType/projectMethod should be READ inside effect, not WATCHED as dependencies
    }, [idFromUrl]);

    // Effect to re-setup form when hasBeenRan changes
    const hasBeenRanRef = useRef(projectData.hasBeenRan);
    useEffect(() => {
        const hasDesignSections = 'surfaceDesign' in formConfig.groupedFields || 'baseDesign' in formConfig.groupedFields || 'subbaseDesign' in formConfig.groupedFields;

        // 🔍 DEBUG: Trace surface.minThickness in form re-setup effect
        console.log('🔍 DEBUG: Form re-setup effect triggered', {
            hasBeenRan: projectData.hasBeenRan,
            hasBeenRanRefCurrent: hasBeenRanRef.current,
            hasDesignSections,
            willTriggerResetup: projectData.hasBeenRan && !hasBeenRanRef.current && !hasDesignSections,
            surfaceMinThicknessInRedux: (projectData.projectData?.projectInfo?.data as any)?.surface?.minThickness,
            conversionApplied: projectData.projectData?.conversionApplied,
            projectUnit: projectData.projectUnit,
        });

        if (projectData.hasBeenRan && !hasBeenRanRef.current && !hasDesignSections) {
            if (projectData.projectType && projectData.projectMethod) {
                console.log('🔍 DEBUG: About to call setupFormForMethod with data', {
                    surfaceMinThicknessInData: (projectData.projectData?.projectInfo?.data as any)?.surface?.minThickness,
                    hasSurfaceInData: 'surface' in (projectData.projectData?.projectInfo?.data || {}),
                });
                setupFormForMethod(projectData.projectMethod, projectData.projectType, projectData.projectUnit, projectData.projectData?.projectInfo?.data).catch(error => {
                    console.error('[MODULAR_HOOK] Error re-setting up form after hasBeenRan change:', error);
                });
            }
        }

        hasBeenRanRef.current = projectData.hasBeenRan;
    }, [projectData.hasBeenRan, projectData.projectType, projectData.projectMethod, projectData.projectUnit, formConfig.groupedFields]);

    // Handle method change
    // currentFormValues: The current Formik form values (user's unsaved edits)
    const handleMethodChange = useCallback(
        async (newMethod: string, oldMethod: string, setFieldValue: (field: string, value: any) => void, currentFormValues?: Record<string, any>) => {
            const message = t('change_method_message') || 'Are you sure you want to change the method? This might reset some relevant data.';

            const confirmAction = async () => {
                formDialog.closeConfirmationDialog();
                setIsMethodChanging(true);
                projectLoader.setLoading(true);
                formDialog.clearError();

                const currentType = projectData.projectType;
                const currentUnit = projectData.projectUnit;

                if (!currentType) {
                    console.error('[MODULAR_HOOK] Cannot change method: current project type is unknown');
                    formDialog.setErrorDialog('error_unknown_project_type');
                    projectLoader.setLoading(false);
                    setIsMethodChanging(false);
                    setFieldValue('projectSettings.calculationMethod', oldMethod);
                    return;
                }

                try {
                    // setupFormForMethod now returns the finalData directly (without waiting for React state)
                    const initialDataForNewMethod = await setupFormForMethod(newMethod, currentType, currentUnit);

                    if (projectData.projectData && initialDataForNewMethod) {
                        // 🔧 FIX: Use currentFormValues if available (user's unsaved edits)
                        // Otherwise fall back to Redux state (for backwards compatibility)
                        const prevProjectInfoData = currentFormValues || projectData.projectData.projectInfo?.data || {};
                        const { type, ...rest } = prevProjectInfoData as any;
                        const cleanedProjectInfoData: any = { type };

                        Object.keys(rest).forEach(key => {
                            if (key !== oldMethod && key !== newMethod) {
                                cleanedProjectInfoData[key] = rest[key];
                            }
                        });

                        // Deep clone to ensure we can modify the nested objects
                        cleanedProjectInfoData[newMethod] = JSON.parse(JSON.stringify(initialDataForNewMethod));
                        // CRITICAL: Set method field in projectInfo.data (required by backend)
                        cleanedProjectInfoData.method = newMethod;

                        // 🆕 BUG FIX: Migrate common fields from old method to new method
                        // When switching methods (e.g., Static → BCR), preserve user-entered data
                        // for fields that exist in both methods
                        //
                        // IMPORTANT: Data structure is FLAT (PD3/PD4), so fields are at top level of prevProjectInfoData
                        // NOT nested under prevProjectInfoData[oldMethod]

                        // For Container Yard/Parking Deck: only fsDesign is common between Static and BCR
                        // (load, subgrade fields have DIFFERENT names/structures between methods)
                        const commonFieldsToMigrate = ['fsDesign'];

                        commonFieldsToMigrate.forEach(fieldName => {
                            // Read from FLAT structure (top level)
                            const fieldValue = (prevProjectInfoData as any)[fieldName];

                            if (fieldValue !== undefined) {
                                // Migrate field to new method (both FLAT and nested for backward compatibility)
                                cleanedProjectInfoData[fieldName] = fieldValue; // FLAT
                                cleanedProjectInfoData[newMethod][fieldName] = fieldValue; // nested
                            }
                        });

                        // UNPAVED-specific field migration (Giroud ↔ Leng)
                        // These fields have the same name in both methods but different defaults
                        if (currentType === EProjectType.unpaved) {
                            const prevLoad = (prevProjectInfoData as any).load || {};
                            const prevSubgrade = (prevProjectInfoData as any).subgrade || {};

                            // Deep clone load and subgrade to ensure we can modify them
                            // (Redux objects are frozen, so we need mutable copies)
                            cleanedProjectInfoData.load = JSON.parse(JSON.stringify(cleanedProjectInfoData[newMethod].load || {}));
                            cleanedProjectInfoData.subgrade = JSON.parse(JSON.stringify(cleanedProjectInfoData[newMethod].subgrade || {}));

                            // Migrate load fields with same names
                            const loadFieldsToMigrate = ['tyreInflationPressure', 'esal', 'allowableRutDepth'];
                            loadFieldsToMigrate.forEach(fieldName => {
                                if (prevLoad[fieldName] !== undefined) {
                                    cleanedProjectInfoData.load[fieldName] = prevLoad[fieldName];
                                    cleanedProjectInfoData[newMethod].load[fieldName] = prevLoad[fieldName];
                                }
                            });

                            // Map wheelLoad (Giroud) ↔ designAxleLoad (Leng)
                            // Axle load = 2 * wheel load (axle has 2 wheels)
                            if (oldMethod === 'giroud' && newMethod === 'leng' && prevLoad.wheelLoad !== undefined) {
                                const axleLoad = prevLoad.wheelLoad * 2;
                                cleanedProjectInfoData.load.designAxleLoad = axleLoad;
                                cleanedProjectInfoData[newMethod].load.designAxleLoad = axleLoad;
                            } else if (oldMethod === 'leng' && newMethod === 'giroud' && prevLoad.designAxleLoad !== undefined) {
                                const wheelLoad = prevLoad.designAxleLoad / 2;
                                cleanedProjectInfoData.load.wheelLoad = wheelLoad;
                                cleanedProjectInfoData[newMethod].load.wheelLoad = wheelLoad;
                            }

                            // Migrate all subgrade fields (same names in both methods)
                            const subgradeFieldsToMigrate = ['subgradeInputMethod', 'cbrBaseCourse', 'cbrSubgrade', 'ev2BaseCourse', 'ev2Subgrade'];
                            subgradeFieldsToMigrate.forEach(fieldName => {
                                if (prevSubgrade[fieldName] !== undefined) {
                                    cleanedProjectInfoData.subgrade[fieldName] = prevSubgrade[fieldName];
                                    cleanedProjectInfoData[newMethod].subgrade[fieldName] = prevSubgrade[fieldName];
                                }
                            });

                            // ✅ CRITICAL FIX: Update validatedData with migrated values
                            // Formik uses validatedData as initialValues. Without this update,
                            // Formik would show the new method's defaults instead of migrated user values.
                            const migratedDataForFormik = {
                                ...initialDataForNewMethod,
                                load: cleanedProjectInfoData.load,
                                subgrade: cleanedProjectInfoData.subgrade,
                            };
                            formValidation.setValidatedData(migratedDataForFormik);
                        }

                        dispatch(
                            setProject({
                                ...projectData.projectData,
                                method: newMethod,
                                projectInfo: {
                                    ...projectData.projectData.projectInfo,
                                    data: cleanedProjectInfoData,
                                },
                            })
                        );

                        setFieldValue('projectSettings.calculationMethod', newMethod);
                    }
                } catch (error) {
                    console.error('[MODULAR_HOOK] Error changing method:', error);
                    setFieldValue('projectSettings.calculationMethod', oldMethod);
                    formDialog.setErrorDialog('error_changing_method');
                } finally {
                    projectLoader.setLoading(false);
                    setIsMethodChanging(false);
                }
            };

            const cancelAction = () => {
                formDialog.closeConfirmationDialog();
                setFieldValue('projectSettings.calculationMethod', oldMethod);
            };

            formDialog.openConfirmationDialog(t('confirm_method_change_title') || 'Confirm Method Change', message, confirmAction, cancelAction);
        },
        [t, dispatch, projectData.projectData, projectData.projectType, projectData.projectUnit, formValidation.validatedData, formDialog, projectLoader, setupFormForMethod]
    );

    // Wrapper for updateGroupedFieldsForDesignChange
    const updateGroupedFieldsForDesignChange = useCallback(
        async (formValues: any) => {
            await formConfig.updateGroupedFieldsForDesignChange(formValues, projectData.projectType, projectData.projectMethod, projectData.projectUnit, setupFormForMethod);
        },
        [formConfig, projectData.projectType, projectData.projectMethod, projectData.projectUnit, setupFormForMethod]
    );

    // Return combined API
    return {
        // Loading states
        loading,
        errorDialog: formDialog.errorDialog || projectLoader.loadError,
        setErrorDialog: formDialog.setErrorDialog,

        // Form configuration
        groupedFields: formConfig.groupedFields,
        accordionTitles: formConfig.accordionTitles,

        // Validation
        validationSchema: formValidation.validationSchema,
        validatedData: formValidation.validatedData,

        // Method change
        isMethodChanging,
        handleMethodChange,

        // Project data
        projectDataFromState: projectData.projectData,
        projectUnitFromState: projectData.projectUnit,
        isNewProject: projectData.isNewProject,

        // Method sync
        registerMethodSyncCallback: methodSync.registerMethodSyncCallback,

        // Dialog state
        confirmDialogState: formDialog.confirmDialogState,

        // Design sections update
        updateGroupedFieldsForDesignChange,
    };
}

export type UseProjectFormSetupModularReturn = ReturnType<typeof useProjectFormSetupModular>;
